// Earnings-date feed (D-091) — scheduled per-instrument earnings dates, the twin
// of the econ-calendar (D-065). An earnings DATE is a scheduled calendar fact
// (like FOMC), so it's Kernel-clean: the marker/operand says "earnings is due",
// not what the result will be (the earnings RESULT / surprise is the news layer,
// D-072, and stays behind the classifier). The SAME list feeds the chart's axis
// marker, the in-app backtest, and the box, so a "within N days of earnings" rule
// resolves identically live and in backtest (backtest == live, D-064).
//
// STATUS — surface only. The earnings-date SOURCE was removed from the app with
// the old fundamentals feature, so these return nothing today. Wiring a source is
// the remaining depth: a keyless upcoming-earnings fetch (Yahoo quoteSummary
// calendarEvents) lights the chart marker + live/forward rules; the full historical
// dates needed to BACKTEST an earnings rule come with the deterministic
// `earningsEvent` feed (the D-073 family). Both plug in here without touching the
// builder or chart again.

import { storageGet } from "./storage.js";

export interface EarningsEvent { at: number; symbol: string; name?: string; kind?: "earnings" | "exdiv"; }

// --- Upcoming instrument events via Yahoo (D-091) --------------------------
// Keyless, best-effort fetch of the NEXT earnings date + ex-dividend date from
// Yahoo's quoteSummary endpoint — the same free-aggregate source the chart uses
// for candles (yahoo-source.ts). Undocumented and may be rate-limited/blocked, so
// this is DEFENSIVE: any failure resolves to [] and the chart simply shows no
// instrument markers. Gives the CHART marker + forward/live rules a source;
// BACKTESTING an earnings rule still needs historical dates (the earningsEvent
// feed, D-073) — this endpoint only returns the upcoming ones.
interface YahooCalendarEvents {
  quoteSummary?: { result?: Array<{ calendarEvents?: {
    earnings?: { earningsDate?: Array<{ raw?: number }> };
    exDividendDate?: { raw?: number };
  } }> };
}
async function fetchCalendarFrom(host: string, symbol: string): Promise<YahooCalendarEvents> {
  const url = `https://${host}/v10/finance/quoteSummary/${encodeURIComponent(symbol)}?modules=calendarEvents`;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 12_000);
  try {
    const res = await fetch(url, { signal: controller.signal });
    if (!res.ok) throw new Error(`quoteSummary ${res.status}`);
    return (await res.json()) as YahooCalendarEvents;
  } finally {
    clearTimeout(timeout);
  }
}

/** Finnhub earnings calendar (D-091a) — reliable, needs the user's free key. Returns
 * upcoming earnings dates for `symbol`; [] on any failure or no key. */
async function fetchFinnhubEarnings(symbol: string, key: string): Promise<EarningsEvent[]> {
  const from = new Date().toISOString().slice(0, 10);
  const to = new Date(Date.now() + 120 * 86_400_000).toISOString().slice(0, 10);
  const url = `https://finnhub.io/api/v1/calendar/earnings?from=${from}&to=${to}&symbol=${encodeURIComponent(symbol)}&token=${encodeURIComponent(key)}`;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 12_000);
  try {
    const res = await fetch(url, { signal: controller.signal });
    if (!res.ok) { console.debug("[TickAhead] Finnhub earnings HTTP", res.status, "for", symbol); return []; }
    const data = (await res.json()) as { earningsCalendar?: Array<{ date?: string }> };
    const out: EarningsEvent[] = [];
    for (const e of data.earningsCalendar ?? []) {
      const at = e.date ? Date.parse(`${e.date}T20:00:00Z`) : NaN; // ~after the US close
      if (Number.isFinite(at)) out.push({ at, symbol, name: "Earnings", kind: "earnings" });
    }
    return out;
  } catch { return []; } finally { clearTimeout(timeout); }
}

/** Yahoo quoteSummary fallback (unofficial, often gated) — earnings + ex-dividend. */
async function fetchYahooUpcoming(symbol: string): Promise<EarningsEvent[]> {
  let data: YahooCalendarEvents;
  try {
    try { data = await fetchCalendarFrom("query1.finance.yahoo.com", symbol); }
    catch { data = await fetchCalendarFrom("query2.finance.yahoo.com", symbol); }
  } catch { return []; }
  const cal = data.quoteSummary?.result?.[0]?.calendarEvents;
  if (!cal) return [];
  const out: EarningsEvent[] = [];
  for (const d of cal.earnings?.earningsDate ?? []) {
    if (d.raw) out.push({ at: d.raw * 1000, symbol, name: "Earnings", kind: "earnings" });
  }
  if (cal.exDividendDate?.raw) out.push({ at: cal.exDividendDate.raw * 1000, symbol, name: "Ex-dividend", kind: "exdiv" });
  return out;
}

/** Upcoming earnings (+ ex-dividend) for `symbol`, for chart markers. Uses the
 * user's Finnhub key when set (reliable); otherwise falls back to Yahoo
 * best-effort (usually gated). Empty on any failure. */
export async function fetchUpcomingInstrumentEvents(symbol: string): Promise<EarningsEvent[]> {
  const key = (await storageGet<string>("finnhubKey", "")) || "";
  if (!key) console.debug("[TickAhead] no Finnhub key set — add one in Setup → Connections → Earnings data (Yahoo fallback is usually blocked)");
  const primary = key ? await fetchFinnhubEarnings(symbol, key) : [];
  const events = primary.length ? primary : await fetchYahooUpcoming(symbol);
  const now = Date.now();
  return events.filter((e) => e.at > now - 86_400_000); // drop stale
}

/**
 * Earnings dates overlapping [fromMs, toMs] for `symbol`, for the in-app backtest
 * (fed into evaluate() as econ events tagged kind "earnings"). Empty until a
 * historical-dates source is wired — see the module header.
 */
export function earningsFeedEvents(_fromMs: number, _toMs: number, _symbol?: string): EarningsEvent[] {
  return [];
}

/**
 * Upcoming earnings dates for `symbol`, for the chart's time-axis markers. Empty
 * until an upcoming-earnings source (e.g. Yahoo calendarEvents) is wired.
 */
export function upcomingEarnings(_symbol: string, _limit = 4): EarningsEvent[] {
  return [];
}

/** The earnings-date file the agent writes to the box (`~/.tickahead/earnings.json`),
 * the earnings twin of econCalendarFile (D-065). Empty until the source lands. */
export function earningsFeedFile(now: number = Date.now()): { refreshedAt: number; events: EarningsEvent[] } {
  return { refreshedAt: now, events: [] };
}
